'use client';

import { Session } from 'next-auth';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import {
    Alignable,
    ButtonVariant,
    Column,
    Columns,
    Container,
    IconName,
    Row,
    Rows,
    Section,
    Spacer,
    SpacerSize,
    Tag,
    TagVariant,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { faiService } from '@services/fai';
import { createVirtualCart } from '@services/simulator';
import { setSessionVariable } from '@services/storage';

import { getServerSession } from '@actions/get-session';

import { SapicCategoryId } from '@app-types/api/sapic';
import { QUOTE_TYPES } from '@app-types/cart';
import {
    HomePackCategory,
    HomePackResponseWithCategories,
    HomepackProduct,
    HomepackProductOptions,
    PACK_HASH,
    StaticPackProduct,
} from '@app-types/home-pack';
import { FaiType, FaiTypeWithEligibility, OffersByCategoryCode } from '@app-types/plan';
import { PRICE_TYPE } from '@app-types/price';
import { Product } from '@app-types/product';
import { SimulatedCart } from '@app-types/simulator/virtual-cart';
import { UserType } from '@app-types/user';

import { PromoConfigurator } from '@components/promo-configurator';
import { RocCard } from '@components/roc-card';
import { Ticket } from '@components/ticket';

import { useMarketings } from '@hooks/use-marketings';
import { useOnScreen } from '@hooks/use-on-screen';

import { eligibilityContextualisation } from '@helpers/eligibility';
import { emitPriceContextChangeEvent } from '@helpers/marketings';
import { paths } from '@helpers/path';

import { PlanTypeId } from '@constants/plan';
import { ELIGIBILITY_STORAGE_KEY } from '@constants/provider';

import { ConnectionIncentive } from '@home-fai/components';
import {
    EconomyBlock,
    ProductCard,
    ProductsFilter,
    HomePackTabs as StaticTabs,
    StickyBar,
} from '@home-pack/components';
import {
    PackConfigType,
    sendComboPageTracking,
    sendTabletColorChoiceTracking,
    upperCaseAndAddDash,
} from '@home-pack/helpers';
import { useAddStaticProduct } from '@home-pack/hooks';

import { Hero } from './hero';

type PackBodyProps = {
    defaultPlans: FaiType[];
    config: { [index: string]: PackConfigType };
    staticsCategories?: HomePackResponseWithCategories;
    currentStaticCategory?: HomePackCategory;
    faqQuestionList?: {
        question: string;
        answer: string;
        id: number;
    }[];
    trackingData: {
        page: string;
        marketLine: string;
    };
    offersByCategory: OffersByCategoryCode;
    selectedStaticProduct: StaticPackProduct | null;
    setSelectedStaticProduct: (product: StaticPackProduct) => void;
    setSlug: (slug: string) => void;
    timelineDetailsCms?: string;
};

const defaultSimulatedCart: SimulatedCart = {
    quotes: [{ type: QUOTE_TYPES.ACQUISITION_FIXE, products: [] }],
    price: {
        today: {
            prices: {
                initial: 0,
                final: 0,
                forever: 0,
            },
        },
        monthly: {
            prices: {
                initial: 0,
                final: 0,
                forever: 0,
            },
        },
        deferred: {
            prices: {
                initial: 0,
                final: 0,
                forever: 0,
            },
        },
    },
};

const filterOffers = (
    offers: FaiType[],
    offersByCategory: OffersByCategoryCode,
    slug: PACK_HASH,
    config: { [index: string]: PackConfigType },
) => {
    const categoryToShow: PlanTypeId = config[slug]?.categoryToShow ?? PlanTypeId.FAI_SMART_TV;
    const productsByHash: FaiType[] = [];

    offersByCategory[categoryToShow]?.forEach((codeInfos) => {
        const offer = offers?.find((o) => {
            return (
                o.rangeNg === codeInfos.rangeNg &&
                o.commercialSeries === codeInfos.commercialSeries &&
                o.technology === codeInfos.technology &&
                o.categories.includes(categoryToShow)
            );
        });

        if (offer) {
            productsByHash.push(offer);
        }
    });

    return productsByHash;
};

export function PackBody({
    defaultPlans,
    config,
    staticsCategories,
    currentStaticCategory,
    trackingData,
    setSelectedStaticProduct,
    selectedStaticProduct,
    offersByCategory,
    setSlug,
    timelineDetailsCms,
}: Readonly<PackBodyProps>) {
    const [currentSimulatedCart, setCurrentSimulatedCart] = useState<SimulatedCart>(defaultSimulatedCart);
    const [filteredStaticProductsTechno, setFilteredStaticProductsTechno] = useState<string>('');
    const { ref: hideRef, isVisible: isTicketCTAVisible } = useOnScreen(false);
    const { ref: hideRefIncitation, isVisible: isIncitationVisible } = useOnScreen(true);
    const [marketingsState, setMarketingsState] = useMarketings();
    const [session, setSession] = useState<Session | null>(null);

    const [currentCategory, setCurrentCategory] = useState(currentStaticCategory);
    const currentCategorySlug = currentCategory?.slug as PACK_HASH;
    const staticsProducts = currentCategory?.productsSamsung || [];
    const [plans, setPlans] = useState<FaiTypeWithEligibility[]>(
        filterOffers(defaultPlans, offersByCategory, currentCategorySlug, config),
    );

    let allPlans = defaultPlans;

    const user = session?.user as UserType | undefined;

    useAddStaticProduct(currentSimulatedCart, setCurrentSimulatedCart, selectedStaticProduct);

    const handleSelectProduct = (mainProduct: HomepackProduct, details: HomepackProductOptions) => {
        setSessionVariable(
            ELIGIBILITY_STORAGE_KEY.PACK_PRODUCT,
            JSON.stringify({ ...mainProduct, productSize: [details], slug: currentCategorySlug }),
        );
        setSelectedStaticProduct({
            mainProduct,
            details,
        });
    };

    const handleRedirection = () => {
        if (currentCategorySlug === PACK_HASH.IPAD) {
            const tabletName = selectedStaticProduct?.mainProduct?.name;
            const selectedColor = selectedStaticProduct?.details?.colorName;
            sendTabletColorChoiceTracking(tabletName, selectedColor);
        }
    };

    const onAddBbox = (plan: FaiType) => {
        const updatedSimulatedCart = { ...currentSimulatedCart };
        const product: Partial<FaiType> = {
            gencode: plan.gencode,
            name: plan.name,
            details: {
                price: {
                    initial: plan.details.price.initial + (plan.rentalFees || 0),
                    final: plan.details.price.final + (plan.rentalFees || 0),
                    forever: plan.details.price.forever + (plan.rentalFees || 0),
                    type: PRICE_TYPE.MONTHLY,
                },
                fundings: [],
                discounts: [],
            },
            range: plan.range,
            rangeNg: plan.rangeNg,
            commercialSeries: plan.commercialSeries,
        };

        updatedSimulatedCart.quotes[0]?.products.splice(1, 1, product as Product);
        updatedSimulatedCart.price.monthly.prices = product.details!.price;
        setCurrentSimulatedCart(updatedSimulatedCart);
    };

    const getRocCta = (plan: FaiType) => ({
        text: `${currentSimulatedCart?.quotes[0]?.products[1]?.gencode === plan.gencode ? 'Bbox sélectionnée' : 'Sélectionner cette Bbox'}`,
        onClick: () => onAddBbox(plan),
        color: ButtonVariant.PRIMARY,
        disabled: currentSimulatedCart?.quotes[0]?.products[1]?.gencode === plan.gencode,
        alternativeState: currentSimulatedCart?.quotes[0]?.products[1]?.gencode === plan.gencode && (
            <Tag variant={TagVariant.SUCCESS} iconName={IconName.CHECK_CIRCLE} label='Bbox sélectionnée' />
        ),
    });

    const eligibilityPageUrl = useMemo(() => {
        const bboxAdded = currentSimulatedCart?.quotes[0]?.products[1];

        let focus =
            bboxAdded && 'rangeNg' in bboxAdded ? `${bboxAdded.rangeNg}_${bboxAdded.commercialSeries}_ftth` : undefined;

        if (focus === undefined) {
            const product = plans.find((product) => product.gencode === bboxAdded?.gencode);
            focus = product ? `${product.rangeNg}_${product.commercialSeries}_ftth` : undefined;
        }

        if (bboxAdded && focus) {
            switch (currentCategorySlug) {
                case PACK_HASH.SMART_TV:
                case PACK_HASH.SMART_MONITOR:
                case PACK_HASH.VIDEOPROJECTOR:
                    return `${paths.smartTvEligibility}?focus=${focus}`;
                case PACK_HASH.GAMING:
                case PACK_HASH.PS5:
                    return `${paths.gamingEligibility}?focus=${focus}`;
                case PACK_HASH.TABLET:
                case PACK_HASH.IPAD:
                    return `${paths.tabletEligibility}?focus=${focus}`;
                default:
                    return paths.smartTvEligibility;
            }
        }
        return paths.eligibilityTest;
    }, [currentSimulatedCart, currentCategorySlug]);
    const searchParams = useSearchParams();
    const technoParams = searchParams.get('techno');

    useEffect(() => {
        setFilteredStaticProductsTechno(upperCaseAndAddDash(technoParams || ''));
    }, [technoParams]);

    const showStickyMenu =
        currentCategory && currentSimulatedCart?.quotes[0]?.products[0] && currentSimulatedCart?.quotes[0]?.products[1];

    const hasStaticProductWithFourNuggets = staticsProducts.some((product) => product.productDetails?.length >= 4);
    const hasFaiProductWithFourNuggets = plans.some((plan) => (plan.nuggets?.length || 0) >= 4);
    const categoryTitle = config[currentCategorySlug]?.descriptiveTitle;
    const stepTitle = config[currentCategorySlug]?.pageTitle;
    const titleTicketSection = config[currentCategorySlug]?.titleTicketBlock;
    const filteredStaticProducts = staticsProducts?.filter(
        (product) =>
            filteredStaticProductsTechno.length === 0 ||
            filteredStaticProductsTechno
                ?.split(',')
                .includes(upperCaseAndAddDash(product.techno?.toUpperCase().split(' ').join('-'))),
    );

    useEffect(() => {
        sendComboPageTracking(trackingData);
        getServerSession().then((_session) => {
            setSession(_session);
            void eligibilityContextualisation(
                defaultPlans,
                async () =>
                    faiService.getOffers({
                        filterByTypes: [SapicCategoryId.FAI, SapicCategoryId.FAIM_UNLIMITED],
                        limit: -1,
                    }),
                (newPlans) => {
                    allPlans = newPlans;
                    setPlans(filterOffers(newPlans, offersByCategory, currentCategorySlug, config));
                },
                _session,
            );
        });
    }, []);

    useEffect(() => {
        if (plans[0]) {
            onAddBbox(plans[0]);
        }
    }, [plans]);

    useEffect(() => {
        if (!staticsProducts || staticsProducts.length < 1) {
            return;
        }

        const firstProduct = staticsProducts[0]!;

        const defaultProduct =
            firstProduct.options?.find((option) => option.defaultProduct) ?? firstProduct.options?.[0];

        if (defaultProduct) {
            handleSelectProduct(firstProduct, defaultProduct);
        }
    }, [staticsProducts]);

    useEffect(() => {
        setCurrentCategory(currentStaticCategory);
        setPlans(filterOffers(allPlans, offersByCategory, currentStaticCategory?.slug as PACK_HASH, config));
    }, [currentStaticCategory]);

    return (
        <>
            {!!currentCategory?.hero && (
                <Hero
                    title={currentCategory?.hero.title}
                    subtitle={currentCategory?.hero.subtitle}
                    backgroundImageUrl={currentCategory?.hero.backgroundImageUrl}
                    productImageUrl={currentCategory?.hero.productImageUrl}
                />
            )}
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container>
                    <Rows>
                        <Row>
                            <ConnectionIncentive ref={hideRefIncitation} />
                        </Row>
                        <Row>
                            <StaticTabs
                                currentHash={currentCategorySlug}
                                staticsCategories={staticsCategories}
                                setSlug={setSlug}
                            />
                        </Row>
                        {currentStaticCategory?.savings ? (
                            <Row>
                                <Columns align={Alignable.ALIGNED_CENTER}>
                                    <Column narrow>
                                        <EconomyBlock
                                            categoryTitle={categoryTitle}
                                            economyWording={currentStaticCategory?.savings || ''}
                                            iconName={currentStaticCategory?.icon || IconName.VIDEO_CAMERA}
                                        />
                                    </Column>
                                </Columns>
                            </Row>
                        ) : null}
                        <Row>
                            <Title level={TitleLevels.FOUR} markup={TitleMarkup.H3} marginless>
                                1. {stepTitle || 'Sélectionnez votre produit'}
                            </Title>
                        </Row>
                        {staticsProducts.length > 1 ? (
                            <Row>
                                <ProductsFilter
                                    staticsProducts={staticsProducts}
                                    selectedChips={filteredStaticProductsTechno}
                                />
                            </Row>
                        ) : null}
                        <Row>
                            <Columns multiline mobile>
                                {filteredStaticProducts?.map((product) => (
                                    <Column
                                        key={product.id}
                                        mobileSize={12}
                                        tabletSize={6}
                                        desktopSize={4}
                                        className='fai-offer-card'
                                    >
                                        <ProductCard
                                            product={product}
                                            onSelectProduct={handleSelectProduct}
                                            selectedProduct={selectedStaticProduct || undefined}
                                            withBorder={selectedStaticProduct?.mainProduct?.id === product.id}
                                            hasProductWithFourNuggets={hasStaticProductWithFourNuggets}
                                        />
                                    </Column>
                                ))}
                            </Columns>
                        </Row>
                        <Row>
                            <Title level={TitleLevels.FOUR} markup={TitleMarkup.H3} marginless>
                                2. Choisissez votre Bbox
                            </Title>
                        </Row>
                        <Row>
                            {!session?.user && (
                                <Columns>
                                    <Column mobileSize={12} tabletSize={12} desktopSize={8} className='min-w-fit'>
                                        <PromoConfigurator
                                            isFullwidth
                                            marketingsState={marketingsState}
                                            setMarketingsState={(marketings) => {
                                                const newMarketings = setMarketingsState(marketings);
                                                emitPriceContextChangeEvent(newMarketings);
                                                return newMarketings;
                                            }}
                                        />
                                    </Column>
                                </Columns>
                            )}
                            <Columns multiline mobile>
                                {plans.map((plan) => (
                                    <Column
                                        key={plan.id}
                                        mobileSize={12}
                                        tabletSize={6}
                                        desktopSize={4}
                                        className='fai-offer-card'
                                    >
                                        <RocCard
                                            shadowless={false}
                                            plan={plan}
                                            cta={getRocCta(plan)}
                                            withBorder={
                                                currentSimulatedCart?.quotes[0]?.products[1]?.gencode === plan.gencode
                                            }
                                            hasFaiProductWithFourNuggets={hasFaiProductWithFourNuggets}
                                            user={user}
                                            hasSiblingHat={plans.some((p) => p?.hat !== undefined)}
                                        />
                                    </Column>
                                ))}
                            </Columns>
                        </Row>
                    </Rows>
                </Container>
            </Section>
            {timelineDetailsCms && (
                <Section backgroundColor={TrilogyColor.NEUTRAL_FADE} className='has-text-centered'>
                    <Container>
                        <Cms content={timelineDetailsCms} />
                    </Container>
                </Section>
            )}
            <Section backgroundColor={TrilogyColor.MAIN}>
                <Container>
                    <Title
                        markup={TitleMarkup.H2}
                        typo={TypographyAlign.TEXT_CENTERED}
                        inverted
                        level={TitleLevels.THREE}
                    >
                        {titleTicketSection}
                    </Title>
                    <Spacer size={SpacerSize.SIX} />
                    <Columns align={Alignable.ALIGNED_CENTER}>
                        <Column size={4}>
                            <Ticket
                                isLoading={false}
                                cart={currentSimulatedCart}
                                simulatorPlans={plans}
                                virtualCart={createVirtualCart(true)}
                                hasSubtitles
                                href={eligibilityPageUrl}
                                showTotal={false}
                                hideRef={hideRef}
                                discount={
                                    selectedStaticProduct?.details?.priceStriked &&
                                    selectedStaticProduct?.details?.price
                                        ? selectedStaticProduct?.details?.priceStriked -
                                          selectedStaticProduct?.details?.price
                                        : undefined
                                }
                                onValidate={handleRedirection}
                            />
                        </Column>
                    </Columns>
                </Container>
            </Section>
            {showStickyMenu ? (
                <StickyBar
                    isVisible={!isTicketCTAVisible && !isIncitationVisible}
                    currentSimulatedCart={currentSimulatedCart}
                    currentStaticCategory={currentCategory}
                    href={eligibilityPageUrl}
                    onClick={handleRedirection}
                />
            ) : null}
        </>
    );
}
